import { Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { selectIsLoggedIn } from '../state/auth/auth.selector';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  constructor(private store: Store, private router: Router) {}

  /**
   * Returns the loggedIn$ Observable from the auth Store in order to wait for the Store
   * to get the current logged in/out state. If the user is logged in, the
   * route can be accessed. If the user is logged in, they are redirected to the route specified
   * by pathIfNotLoggedIn. They are also unable to access any routes guarded by AuthGuard, and they
   * will continue to be redirected so long as they are logged out.
   *
   * @param pathIfNotLoggedIn Route to navigate to if the user is not logged in.
   * @returns The loggedIn$ Observable from the auth Store.
   */
  canActivate(
    pathIfNotLoggedIn: string
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.getIsLoggedIn(pathIfNotLoggedIn);
  }

  /**
   * Returns loggedIn$ Observable from the auth Store in order to wait for the Store
   * to get the current logged in/out state. If the user is logged in, the
   * route can be accessed. If the user is logged in, they are redirected to the route specified
   * by pathIfNotLoggedIn. They are also unable to access any routes guarded by AuthGuard, and they
   * will continue to be redirected so long as they are logged out.
   *
   * @param pathIfNotLoggedIn Route to navigate to if the user is not logged in.
   * @returns The loggedIn$ Observable from the auth Store.
   */
  canActivateChild(
    pathIfNotLoggedIn: string
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.getIsLoggedIn(pathIfNotLoggedIn);
  }

  private getIsLoggedIn(pathIfNotLoggedIn: string): Observable<boolean> {
    return this.store.select(selectIsLoggedIn).pipe(
      filter((isLoggedIn: boolean | null) => isLoggedIn !== null),
      map((isLoggedIn) => {
        if (!isLoggedIn) {
          this.router.navigateByUrl(pathIfNotLoggedIn);
          return false;
        } else {
          return true;
        }
      })
    );
  }
}
